/* eslint-disable no-underscore-dangle, max-classes-per-file, no-plusplus, @typescript-eslint/lines-between-class-members, no-return-assign, spaced-comment, prefer-destructuring, @typescript-eslint/ban-types */
import { useEffect, useReducer, useMemo, useRef } from 'react';
import produce, { Draft, Patch, enablePatches } from 'immer';
import isEqual from 'fast-deep-equal/es6/react';

const logger = /*#__PURE__*/ (message: string, ...args: any[]) => {
  const date = new Date().toLocaleString();

  console.log(`%c${date}: %c${message}`, 'color: red', 'color: green', ...args);
};

const info = process.env.NODE_ENV === 'development' ? logger : () => {};
const warn = process.env.NODE_ENV === 'development' ? console.warn : () => {};

/* istanbul ignore else */
if (process.env.NODE_ENV !== 'production') {
  enablePatches();
}

type Selector<T, R = T> = (state: T) => R extends object ? Readonly<R> : R;
type Listener<T> = (state: T) => void;
type Dispatch<T> = (update: Update<T>) => void;
type Update<T> = (state: Draft<T>) => void;

export class ImmutableStateError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImmutableStateError';
  }
}

/*#__PURE__*/ class Trace extends Error {
  constructor() {
    super();
    this.name = 'Trace';
  }
}

class ImmutableStateContext<T> {
  private _name: string;
  private _batchRerenderDelay: number;
  private _current: Readonly<T>;
  private _listeners: Set<Listener<T>> = new Set();

  constructor(name: string, batchRerenderDelay: number, init: T) {
    this._name = name;
    this._batchRerenderDelay = batchRerenderDelay;
    this._current =
      process.env.NODE_ENV !== 'production' ? produce(init, () => {}) : init;
  }

  get name() {
    return this._name;
  }
  get batchRerenderDelay() {
    return this._batchRerenderDelay;
  }
  get listeners() {
    return this._listeners;
  }

  public selectState(selector: Selector<T, any>) {
    return selector(this._current);
  }

  public dispatchUpdate(update: Update<T>) {
    if (process.env.NODE_ENV !== 'production') {
      try {
        let patches: Patch[] = [];
        const next = produce(
          this._current,
          (draft) => {
            update(draft);
          },
          (p) => (patches = patches.concat(p))
        );
        this._current = next;
        console.groupCollapsed(`${this._name} changed`);
        info('Changes:', patches);
        info('Next state:', next);
        console.groupEnd();
        const startTime = performance.now();
        this._listeners.forEach((l) => l(this._current));
        const endTime = performance.now();
        const perfInMs = Math.round((endTime - startTime) * 100) / 100;
        if (perfInMs > 20) {
          info(`${this._name} bad listener performance: ${perfInMs}ms`);
        }
      } catch (e) {
        info(`${this._name} error while dispatching:`, update.toString());
        throw e;
      }
    } else {
      const next = produce(this._current, (draft) => {
        update(draft);
      });
      this._current = next;
      this._listeners.forEach((l) => l(this._current));
    }
  }
}

function defaultSelector<T>(state: T): Readonly<T> {
  return state;
}

function useSelect<T>(
  context: ImmutableStateContext<T>
): React.MutableRefObject<T>;
function useSelect<T, R>(
  context: ImmutableStateContext<T>,
  selector: Selector<T, R>
): React.MutableRefObject<R>;
function useSelect<T, R>(
  context: ImmutableStateContext<T>,
  selector: Selector<T, R> = defaultSelector as any
): React.MutableRefObject<R> {
  const [, forceRerender] = useReducer((r) => ++r, 0);
  const initSelected = useMemo(
    () => context.selectState(selector),
    [selector, context]
  );
  const currSelected = useRef<ReturnType<typeof selector>>(initSelected);
  const batchUpdates = useRef<NodeJS.Timeout | undefined>();
  const checkForInlineSelector = /*#__PURE__*/ useRef<null | typeof selector>(
    null
  );

  const trace =
    /*#__PURE__*/ process.env.NODE_ENV !== 'production'
      ? new Trace()
      : undefined;

  useEffect(() => {
    // Listen to state updates, use selector to determine if component needs rerender
    const listener = (nextState: T) => {
      const nextSelected = selector(nextState);
      if (isEqual(currSelected.current, nextSelected)) return;

      if (process.env.NODE_ENV !== 'production') {
        const changedForFull = trace?.stack
          ?.split('\n')
          .slice(3)
          .shift()
          ?.trim()
          .split('at ')
          .pop();
        const changedForName = changedForFull?.split(' ').shift();
        console.groupCollapsed(
          `${changedForName} selected ${context.name} changed`
        );
        info(`selected ${context.name} changed for:`, changedForFull);
        info('selector', selector.toString());
        console.groupEnd();
      }

      currSelected.current = nextSelected;
      if (context.batchRerenderDelay) {
        if (!batchUpdates.current) {
          batchUpdates.current = setTimeout(() => {
            batchUpdates.current = undefined;
            forceRerender();
          }, context.batchRerenderDelay);
        }
      } else {
        forceRerender();
      }
    };

    const listeners = context.listeners;
    listeners.add(listener);

    return () => {
      listeners.delete(listener);
    };
    // ignore trace here because it will be removed in production
  }, [context, selector]);

  /*#__PURE__*/ useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;

    if (!checkForInlineSelector.current)
      checkForInlineSelector.current = selector;
    if (checkForInlineSelector.current !== selector) {
      const selectorCodeIsEqual =
        checkForInlineSelector.current.toString() === selector.toString();
      const msg = selectorCodeIsEqual
        ? `${
            context.name
          } useSelect selector should not be inlined (bad for performance): ${selector.toString()}`
        : `${
            context.name
          } useSelect selector cannot be defined conditionally (dynamic updating of selectors is not supported): ${selector.toString()}`;

      if (process.env.NODE_ENV === 'test') throw new ImmutableStateError(msg);
      warn(`IF NOT HOT-UPDATE: ${msg}`);
    }
  }, [context, selector]);

  return currSelected;
}

function createUseSelectFor<T>(
  context: ImmutableStateContext<T>
): <R = T>(selector?: Selector<T, R>) => React.MutableRefObject<R>;
function createUseSelectFor<T, R>(context: ImmutableStateContext<T>) {
  return (selector: Selector<T, R>) => useSelect(context, selector);
}

type ImmutableState<T> = {
  useImmutableState: <R = T>(
    selector?: Selector<T, R>
  ) => React.MutableRefObject<R>;
  updateState: Dispatch<T>;
};
export function createImmutableState<T extends object>(
  name: string,
  initialValues: T,
  batchRerenderDelay = 80
): ImmutableState<T> {
  const context = new ImmutableStateContext(
    name,
    batchRerenderDelay,
    initialValues
  );
  return {
    useImmutableState: createUseSelectFor(context),
    updateState: (update: Update<T>) => context.dispatchUpdate(update)
  };
}
